import every from 'lodash/every';

import { FailSignal, ModelValue, OneFormReduxState, RulesAndError, Validator } from '../types';

export const makeValidator =
	<FieldNames extends string>(rules: Validator<FieldNames>[]): Validator<FieldNames> =>
	(value, props) =>
		every(rules, rule => rule(value, props));

/*
  Provide a list of { rules, errorResult }.
  The provided list will be evaluated in order,
  and the rules within a given list { rules, errorResult } tuple will also be evaluated in order.
  If any rule is violated, the function will short circuit, and return the matching errorResult.

  Example:
      validateOrErrorResult([
          { rules: [type('string'), minLength(1)], errorResult: 'Please enter your PayPal email' },
          { rules: [email], errorResult: (value, props) => `${value} is not a valid email` },
      ])
 */
export const validateOrErrorResult =
	<FieldNames extends string>(
		rulesAndErrorResult: RulesAndError<FieldNames>[],
	): ((value: ModelValue | undefined, props: OneFormReduxState<FieldNames>) => string[] | undefined) =>
	(value, props) => {
		const errorResults = [];
		for (let index = 0; index < rulesAndErrorResult.length; index++) {
			const { rules, errorResult } = rulesAndErrorResult[index];
			const validate = makeValidator(rules);
			if (!validate(value, props)) {
				errorResults.push(errorResult instanceof Function ? errorResult(value, props) : errorResult);
			}
		}
		return errorResults.length ? errorResults : undefined;
	};

/*
  Helpful utility function for the validations in your arguments to the form decorator.
  Provide a list of { rules, errorResult }, and this function will return a function that works with useForm.
  The provided list will be evaluated in order,
  and the rules within a given list { rules, errorResult } tuple will also be evaluated in order.
  If any rule is violated, the function will short circuit,
  and return the result of the provided `fail` function called with the matching errorResult.

  Example:
      {
          paypalEmail: validateOrFail([
              { rules: [type('string'), minLength(1)], errorResult: 'Please enter your PayPal email' },
              { rules: [email], errorResult: (value, props) => `${value} is not a valid email` },
          ])
      }
 */
export const validateOrFail =
	<FieldNames extends string>(
		rulesAndErrorResult: RulesAndError<FieldNames>[],
	): ((value: ModelValue | undefined, fail: FailSignal, props: OneFormReduxState<FieldNames>) => void) =>
	(value, fail, props): void => {
		const errorResult = validateOrErrorResult(rulesAndErrorResult)(value, props);
		if (errorResult) {
			return fail(errorResult);
		}
		// signals no invalid values found
		return undefined;
	};
